import React, { useState } from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  Stack,
  LinearProgress,
  Paper,
  Divider,
  Fade,
  Zoom,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Whatshot as FireIcon,
  LocalFireDepartment as FlameIcon,
} from '@mui/icons-material';
import { alpha, useTheme, keyframes } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────────────────────
// TIER CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────────
const TIER_CONFIG = {
  'Elite': {
    color: '#FFD700',
    gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    shadowColor: 'rgba(255, 215, 0, 0.4)',
    icon: TrophyIcon,
    label: 'Elite',
    description: 'Top 10% of creators',
  },
  'Rising Star': {
    color: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 100%)',
    shadowColor: 'rgba(156, 39, 176, 0.4)',
    icon: StarIcon,
    label: 'Rising Star',
    description: 'High-performing creator',
  },
  'Established': {
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)',
    shadowColor: 'rgba(33, 150, 243, 0.4)',
    icon: TrendingUpIcon,
    label: 'Established',
    description: 'Solid track record',
  },
  'Emerging': {
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
    shadowColor: 'rgba(76, 175, 80, 0.4)',
    icon: FlameIcon,
    label: 'Emerging',
    description: 'Growing presence',
  },
  'Newcomer': {
    color: '#757575',
    gradient: 'linear-gradient(135deg, #757575 0%, #9E9E9E 100%)',
    shadowColor: 'rgba(117, 117, 117, 0.3)',
    icon: TrendingUpIcon,
    label: 'Newcomer',
    description: 'Just getting started',
  },
};

const FACTOR_LABELS = {
  profile_completeness: 'Profile Quality',
  social_metrics: 'Social Reach',
  engagement_reviews: 'Engagement',
  platform_diversity: 'Multi-Platform',
  recency_activity: 'Recent Activity',
  partner_bonus: 'Partner Status',
};

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATIONS
// ─────────────────────────────────────────────────────────────────────────────
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px currentColor, 0 0 10px currentColor;
  }
  50% {
    box-shadow: 0 0 10px currentColor, 0 0 20px currentColor, 0 0 30px currentColor;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TOOLTIP COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const RankingTooltip = ({ ranking }) => {
  const theme = useTheme();
  const { score, tier, top_factors = [] } = ranking;
  const config = TIER_CONFIG[tier] || TIER_CONFIG['Newcomer'];
  const TierIcon = config.icon;

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return theme.palette.success.main;
    if (percentage >= 75) return theme.palette.info.main;
    if (percentage >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Paper
      elevation={8}
      sx={{
        p: 2.5,
        minWidth: 280,
        maxWidth: 320,
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${alpha(config.color, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
          : `linear-gradient(135deg, ${alpha(config.color, 0.05)} 0%, #ffffff 100%)`,
        border: `1px solid ${alpha(config.color, 0.2)}`,
        boxShadow: `0 8px 32px ${alpha(config.color, 0.15)}`,
      }}
    >
      <Stack spacing={2.5}>
        {/* Header */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: config.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${config.shadowColor}`,
              }}
            >
              <TierIcon sx={{ fontSize: 20, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Marketplace Ranking
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                Rank Score
              </Typography>
            </Box>
          </Stack>

          {/* Score Display */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: config.gradient,
              position: 'relative',
              overflow: 'hidden',
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                backgroundSize: '200% 100%',
                animation: `${shimmer} 3s infinite`,
              }}
            />
            <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ position: 'relative' }}>
              <Typography variant="h2" fontWeight={900} sx={{ color: 'white', lineHeight: 1 }}>
                {score}
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5 }}>
                / 100
              </Typography>
            </Stack>
          </Box>

          {/* Tier Badge */}
          <Chip
            label={config.label}
            size="small"
            sx={{
              background: alpha(config.color, 0.15),
              color: config.color,
              fontWeight: 700,
              borderRadius: 2,
              border: `1.5px solid ${alpha(config.color, 0.3)}`,
              fontSize: '0.75rem',
              height: 26,
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {config.description}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: alpha(config.color, 0.1) }} />

        {/* Top Factors */}
        {top_factors.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Top Ranking Factors
            </Typography>
            <Stack spacing={1.5}>
              {top_factors.map((factor, idx) => {
                const percentage = factor.percentage || 0;
                const scoreColor = getScoreColor(percentage);
                
                return (
                  <Box key={idx}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                        {FACTOR_LABELS[factor.name] || factor.name}
                      </Typography>
                      <Chip
                        label={`${factor.score}/${factor.max}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: alpha(scoreColor, 0.1),
                          color: scoreColor,
                          border: `1px solid ${alpha(scoreColor, 0.2)}`,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Stack>
                    <Box sx={{ position: 'relative' }}>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(theme.palette.grey[400], 0.15),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            background: `linear-gradient(90deg, ${alpha(scoreColor, 0.8)} 0%, ${scoreColor} 100%)`,
                            boxShadow: `0 0 10px ${alpha(scoreColor, 0.3)}`,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          right: 4,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: percentage > 50 ? 'white' : scoreColor,
                          textShadow: percentage > 50 ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
                        }}
                      >
                        {Math.round(percentage)}%
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
            Rankings update daily based on profile activity
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN BADGE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export const RankingBadge = ({ ranking, size = 'small', variant = 'icon' }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  if (!ranking || !ranking.score) return null;

  const { score, tier } = ranking;
  const config = TIER_CONFIG[tier] || TIER_CONFIG['Newcomer'];
  const TierIcon = config.icon;
  const isElite = tier === 'Elite';
  const isRisingStar = tier === 'Rising Star';

  // ─────────────────────────────────────────────────────────────────────────
  // ICON VARIANT
  // ─────────────────────────────────────────────────────────────────────────
  if (variant === 'icon') {
    return (
      <Tooltip
        title={<RankingTooltip ranking={ranking} />}
        arrow
        placement="right"
        TransitionComponent={Zoom}
        enterDelay={200}
        leaveDelay={100}
      >
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: config.gradient,
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: `0 4px 12px ${config.shadowColor}, inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
            border: `2px solid ${alpha('#fff', 0.3)}`,
            animation: (isElite || isRisingStar) ? `${float} 3s ease-in-out infinite` : 'none',
            
            '&:hover': {
              transform: 'scale(1.15) translateY(-2px)',
              boxShadow: `0 6px 20px ${config.shadowColor}, 0 0 20px ${alpha(config.color, 0.4)}, inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
            },

            '&::before': (isElite || isRisingStar) ? {
              content: '""',
              position: 'absolute',
              top: -3,
              left: -3,
              right: -3,
              bottom: -3,
              borderRadius: '50%',
              background: config.gradient,
              opacity: 0.3,
              animation: `${glow} 2s ease-in-out infinite`,
              zIndex: -1,
            } : {},
          }}
        >
          {/* Score Number */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              fontWeight: 900,
              fontSize: '0.7rem',
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              letterSpacing: -0.5,
            }}
          >
            {score}
          </Typography>

          {/* Icon Overlay on Hover */}
          <Fade in={isHovered}>
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                bgcolor: alpha(config.color, 0.9),
              }}
            >
              <TierIcon sx={{ fontSize: 20, color: 'white' }} />
            </Box>
          </Fade>

          {/* Elite/Rising Star Sparkle Effect */}
          {(isElite || isRisingStar) && (
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: '#FFF',
                boxShadow: `0 0 8px ${config.color}, 0 0 12px ${config.color}`,
                animation: `${glow} 1.5s ease-in-out infinite`,
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CHIP VARIANT
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Tooltip
      title={<RankingTooltip ranking={ranking} />}
      arrow
      placement="bottom"
      TransitionComponent={Zoom}
      enterDelay={200}
    >
      <Chip
        icon={
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: config.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 0.5,
            }}
          >
            <TierIcon sx={{ fontSize: 14, color: 'white' }} />
          </Box>
        }
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight={800}>
              {score}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              •
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {config.label}
            </Typography>
          </Stack>
        }
        size={size}
        sx={{
          background: `linear-gradient(135deg, ${alpha(config.color, 0.12)} 0%, ${alpha(config.color, 0.08)} 100%)`,
          color: config.color,
          border: `1.5px solid ${alpha(config.color, 0.3)}`,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: `0 2px 8px ${alpha(config.color, 0.15)}`,
          height: size === 'small' ? 28 : 32,
          
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(config.color, 0.2)} 0%, ${alpha(config.color, 0.15)} 100%)`,
            borderColor: config.color,
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${alpha(config.color, 0.25)}, 0 0 20px ${alpha(config.color, 0.15)}`,
          },
          
          '& .MuiChip-label': {
            px: 1.5,
          },
        }}
      />
    </Tooltip>
  );
};

export default RankingBadge;